import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { API_URL, apiRequest } from '../config/api';

const ProjectContext = createContext();

export const useProjects = () => {
  const context = useContext(ProjectContext);
  if (!context) {
    throw new Error('useProjects must be used within a ProjectProvider');
  }
  return context;
};

export const ProjectProvider = ({ children }) => {
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [loading, setLoading] = useState(false);


  // Update selectedProject if it doesn't exist in current projects
  useEffect(() => {
    if (selectedProject && !projects.find(p => p.id === selectedProject.id)) {
      setSelectedProject(projects[0] || null);
    }
  }, [projects, selectedProject]);

  const addProject = (project) => {
    const newId = projects.length > 0 ? Math.max(...projects.map(p => p.id)) + 1 : 1;
    const projectToAdd = {
      id: newId,
      ...project,
      progress: 0,
      createdAt: new Date().toISOString()
    };
    setProjects(prev => [...prev, projectToAdd]);
    
    // If no project is selected, select the new one
    if (!selectedProject) {
      setSelectedProject(projectToAdd);
    }
  };

  const updateProject = (projectId, updatedProject) => {
    setProjects(prev => prev.map(project => 
      project.id === projectId 
        ? { ...project, ...updatedProject }
        : project
    ));
  };

  const deleteProject = (projectId) => {
    setProjects(prev => prev.filter(project => project.id !== projectId));
    // If the deleted project was selected, clear the selection
    if (selectedProject && selectedProject.id === projectId) {
      setSelectedProject(null);
    }
  };

  const deleteProjects = (projectIds) => {
    setProjects(prev => prev.filter(project => !projectIds.includes(project.id)));
    // If any of the deleted projects were selected, clear the selection
    if (selectedProject && projectIds.includes(selectedProject.id)) {
      setSelectedProject(null);
    }
  };

  const clearAllProjects = () => {
    setProjects([]);
    setSelectedProject(null);
  };

  const fetchProjects = useCallback(async () => {
    console.log('fetchProjects called');
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      console.log('Token from localStorage:', token ? 'exists' : 'missing');
      const response = await apiRequest(`${API_URL}/api/projects`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      console.log('Projects API response status:', response.status);
      if (response.ok) {
        const projectsData = await response.json();
        console.log('Projects fetched successfully:', projectsData);
        setProjects(projectsData);
        // Auto-select first project if none selected
        if (projectsData.length > 0 && !selectedProject) {
          setSelectedProject(projectsData[0]);
        }
      } else {
        console.error('Failed to fetch projects:', response.statusText);
        setProjects([]);
      }
    } catch (error) {
      console.error('Error fetching projects:', error);
      setProjects([]);
    } finally {
      setLoading(false);
    }
  }, [selectedProject]);

  const value = {
    projects,
    selectedProject,
    setSelectedProject,
    setProjects,
    addProject,
    updateProject,
    deleteProject,
    deleteProjects,
    clearAllProjects,
    fetchProjects,
    loading
  };

  return (
    <ProjectContext.Provider value={value}>
      {typeof children === 'function' ? children(value) : children}
    </ProjectContext.Provider>
  );
};
